import React, { useEffect, useRef, useState } from 'react';
import styles from './AgentRow.module.scss';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Button from 'component/atom/forms/button/Button';
import Checkbox from 'component/atom/forms/checkbox/Checkbox';
import Select from 'component/atom/forms/select/select/Select';
import { usePost } from 'hooks/usePost';
import { Agent } from 'hooks/endpoints';
import Modal from 'component/hoc/modal/Modal';
import FieldForm from './form/FieldForm';
import { useToast } from 'app-context/toasterContext/ToastContext';
import { formatLastSync } from 'helpers/formatTimeSync';
import { useFetch } from 'hooks/useFetch';
import { useNavigate, useParams } from 'react-router-dom';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import moment from 'moment';

const selectOptions = [
  { label: 'Manual', value: 0 },
  { label: 'Daily', value: 1 },
  { label: 'Weekly', value: 2 },
  { label: 'Monthly', value: 3 },
];

const AgentRow = ({
  record,
  index,
  isSelected,
  formik,
  onToggleSelect,
  refetchAgentData,
  assignedTaskId,
  bulkRunNonce,
  suppressRefetch,
  onSyncComplete,
  testStatus,
  setTestStatuses,
  setIsSyncingAll,
  isAgentConnected,
}) => {
  const pollRef = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const formRef = useRef(null);

  const toast = useToast();

  const status = testStatus || 'Untested';
  const [taskId, setTaskId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [isFieldModalOpen, setIsFieldModalOpen] = useState(false);
  const [selectedFields, setSelectedFields] = useState(null);
  const [resetTrigger, setResetTrigger] = useState(false);
  const [forceSyncFailed, setForceSyncFailed] = useState(false);
  const { mutate: saveConnections, isPending } = usePost();

  const { mutate: testConnection, isPending: isPendingTesting } = usePost({
    onSuccessToast: 'Connection Successful',
    toastType: 'add',
    onErrorToast: 'Error Testing',
  });
  const { mutate: forceSync, isPending: isPendingSync } = usePost({
    onErrorToast: 'Error Syncing',
  });
  const { data: task, refetch } = useFetch(
    [`taskTimer`, taskId],
    Agent.GetTaskStatus(taskId),
    { enabled: !!taskId }
  );

  useEffect(() => {
    const syncItem = record?.details?.sync?.[0];
    if (!syncItem) return;

    const current = Number(syncItem?.percentCompleted ?? 0);
    const total = Math.max(1, Number(syncItem?.total ?? 100));
    const pct = Math.min(100, Number(((current / total) * 100).toFixed(2)));

    const statusCode = Number(syncItem?.status);
    const isComplete = statusCode === 2 || pct >= 100;

    if (!isComplete) {
      setSyncProgress(pct);
      setSyncing(true);
      const maybeTaskId = syncItem?.taskId ?? syncItem?.id;
      if (maybeTaskId) setTaskId(maybeTaskId);
    }
  }, [
    record?.details?.sync?.[0]?.id,
    record?.details?.sync?.[0]?.taskId,
    record?.details?.sync?.[0]?.status,
    record?.details?.sync?.[0]?.percentCompleted,
    record?.details?.sync?.[0]?.total,
  ]);

  useEffect(() => {
    if (!bulkRunNonce) return;
    if (!assignedTaskId) return;
    setSyncProgress(0);
    setSyncing(true);
    setTaskId(assignedTaskId);
  }, [bulkRunNonce, assignedTaskId]);

  const handleTestConnection = () => {
    testConnection(
      {
        url: Agent.testConnection(record?.details?.detail?.id),
      },
      {
        onSuccess: (res) => {
          if ('Success' === res?.data?.description) {
            // setTestStatus('Healthy');
            setTestStatuses((prev) => ({
              ...prev,
              [record.id]: 'Healthy',
            }));
            toast.open('Connection is Healthy', 'add');
          } else {
            // setTestStatus('Failed');
            setTestStatuses((prev) => ({
              ...prev,
              [record.id]: 'Failed',
            }));
            toast.open('Connection is Unhealthy', 'error');
          }
        },
        onError: (err) => {
          setTestStatuses((prev) => ({
            ...prev,
            [record.id]: 'Failed',
          }));
          // setTestStatus('Failed');
        },
      }
    );
  };
  const handleForceSync = () => {
    setSyncProgress(0);
    setSyncing(true);
    forceSync(
      {
        url: Agent.SyncConnection,
        data: { agentConnectionDetailId: record?.details?.detail?.id },
      },
      {
        onSuccess: (res) => {
          const id = res?.data?.taskId;
          setForceSyncFailed(false);
          if (!id) {
            setSyncing(false);
            toast.open('No taskId returned from server', 'error');
            return;
          }
          setTaskId(id);
        },
        onError: () => {
          setForceSyncFailed(true);
          setSyncing(false);
          toast.open('Failed to start sync', 'error');
        },
      }
    );
  };
  const handleRowClick = (e) => {
    const tagName = e.target.tagName.toLowerCase();
    if (
      tagName === 'input' ||
      tagName === 'button' ||
      tagName === 'select' ||
      e.target.closest('label')
    ) {
      return;
    }
    setIsFieldModalOpen(true);
  };
  const stopPolling = (silent = false) => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
      if (!suppressRefetch) {
        refetchAgentData();
      }
    }
    setSyncing(false);
    setTaskId(null);
    if (!silent) {
      const timestamp = moment().format('DD/MM/YYYY');
      setIsSyncingAll(false);
      toast.open(`Sync completed at ${timestamp}`, 'add');
    }
    if (onSyncComplete) onSyncComplete();
  };

  const readProgressAndMaybeStop = (taskResponse) => {
    const current = taskResponse?.data?.percentCompleted ?? 0;
    const total = taskResponse?.data?.total ?? 100;

    const percentageOfCompletion = Number(((current / total) * 100).toFixed(2));
    setSyncProgress(percentageOfCompletion);

    if (percentageOfCompletion >= 100) {
      setTimeout(() => {
        stopPolling();
      }, 500);

      return true;
    }
    return false;
  };
  const startPolling = async () => {
    try {
      const response = await refetch();
      const latestTaskStatus = response?.data ?? task?.data;

      if (readProgressAndMaybeStop(latestTaskStatus)) return;

      pollRef.current = setInterval(async () => {
        try {
          const pollResponse = await refetch();
          const currentTaskStatus = pollResponse?.data ?? task?.data;

          readProgressAndMaybeStop(currentTaskStatus);
        } catch (error) {
          stopPolling(true);
          toast.open('Sync polling stopped due to an error', 'error');
        }
      }, 5000);
    } catch (error) {
      stopPolling(true);
      toast.open('Could not start polling', 'error');
    }
  };

  useEffect(() => {
    if (!taskId || !syncing) return;

    if (pollRef.current) clearInterval(pollRef.current);

    startPolling();

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [taskId, syncing]);

  return (
    <>
      <div
        className={`${styles.row} ${index !== 0 ? styles.withBorder : ''} ${
          status === 'Failed' && styles.failedRow
        }`}
        onClick={handleRowClick}
      >
        <div className={styles['left-column-container']}>
          <div className={styles['left-column']}>
            <div className={styles['title-container']}>
              <div className={styles['checkbox']}>
                <Checkbox
                  ischecked={isSelected}
                  onChange={(e, event) => {
                    event.stopPropagation();
                    onToggleSelect(record.id);
                  }}
                  boxClassName={styles['custom-checkbox']}
                />
              </div>
              <span
                className={`${
                  status === 'Healthy'
                    ? styles.textSuccess
                    : status === 'Failed'
                    ? styles.textFailed
                    : ''
                } ${styles['record-title']}`}
              >
                {record.title}
              </span>
            </div>

            <div className={styles['title-cont']}>
              <div className={styles['title']}>
                <div
                  className={`${styles.custom} ${styles.status} ${
                    status === 'Healthy'
                      ? styles.testSuccess
                      : status === 'Failed'
                      ? styles.testError
                      : styles.testUnknown
                  }`}
                >
                  <div
                    className={`${styles.circle} ${
                      status === 'Healthy'
                        ? styles.healthy
                        : status === 'Failed'
                        ? styles.failed
                        : styles.unknown
                    }`}
                  ></div>
                  {isPendingTesting
                    ? 'Testing Connection...'
                    : status || 'Untested'}
                </div>
                <div className={`${styles.custom} ${styles.api}`}>
                  {record.endpoint}
                </div>
                <div className={`${styles.custom} ${styles.type}`}>
                  {record.type === 1 ? 'Read' : ''}
                </div>
              </div>
            </div>
          </div>
          <span className={styles['record-description']}>
            {record.description}
          </span>
        </div>

        <div className={styles['right-column']}>
          <div className={styles['sync-container']}>
            <div className={styles.sync}>
              <SvgWithBranding type='clock' />
              <p className={styles.syncText}>
                Last sync: {formatLastSync(record.lastSyncTime)}
              </p>
            </div>
            {record.lastSyncTime && (
              <Tooltip content='Data'>
                <SvgWithBranding
                  type='tableGrid'
                  color={`${
                    status === 'Healthy'
                      ? '#ADC4FF'
                      : status === 'Failed'
                      ? '#F36969'
                      : '#C8C8C8'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/bot/data-preview/${id}`, {
                      state: { detailId: record?.details?.detail?.id },
                    });
                  }}
                />
              </Tooltip>
            )}
          </div>
          <div className={styles['buttons']}>
            <div onClick={(e) => e.stopPropagation()}>
              <Select
                name={`pullFrequencies.${record.id}`}
                value={formik.values.pullFrequencies[record.id] ?? ''}
                options={selectOptions}
                onChange={formik.handleChange}
                placeholder='Select'
                classname={styles['daily-select']}
                selectClassname={styles.select}
                onClick={(e) => e.stopPropagation()}
                variant='secondary'
                optionClassname={styles.whiteOptions}
                optionsClassName={styles.options}
                inputClassName={styles.selectInput}
              />
            </div>
            <Button
              label={isPendingTesting ? 'Testing...' : 'Test Connection'}
              type='testConnection'
              transparent
              onClick={(e) => {
                e.stopPropagation();

                handleTestConnection(record);
              }}
              disabled={
                !record?.details?.detail ||
                isPendingTesting ||
                1 !== isAgentConnected
              }
              variant='light-rev'
            />
            <Button
              label={
                syncing
                  ? 'Syncing...'
                  : forceSyncFailed
                  ? 'Retry Force Sync'
                  : 'Force Sync'
              }
              type='sync'
              transparent
              onClick={(e) => {
                e.stopPropagation();
                handleForceSync(record);
              }}
              disabled={
                !record?.details?.detail ||
                syncing ||
                !isSelected ||
                1 !== isAgentConnected
              }
              variant='primary-rev'
            />
          </div>
        </div>
      </div>
      {syncing && (
        <div className={styles.fullSyncBarWrapper}>
          <div className={styles.syncingBarContainer}>
            <div
              className={styles.syncingBar}
              style={{ width: `${syncProgress}%` }}
            ></div>
          </div>
          <div className={styles.syncPercentText}>{syncProgress}% complete</div>
        </div>
      )}
      <Modal
        title='Fields'
        isOpen={isFieldModalOpen}
        onClose={() => setIsFieldModalOpen(false)}
        disabledFooter={!isSelected}
        onSubmit={() => formRef.current?.submit()}
        isLoading={isPending}
        modalContent={{
          component: (
            <FieldForm
              agentData={record?.connectionInfo}
              selectedFields={selectedFields}
              setSelectedFields={setSelectedFields}
              resetTrigger={resetTrigger}
              ref={formRef}
              onSubmitted={() => {
                setIsFieldModalOpen(false);
              }}
              saveConnections={saveConnections}
              refetchAgentData={refetchAgentData}
              getCount={(fromAgent) => {
                setSelectedFields(fromAgent);
              }}
            />
          ),
        }}
        onSubmitstyles={{
          onSubmit: {
            label: 'Save',
            type: 'save',
            opicBAckground: true,
          },
        }}
        exitComponent={
          <div className={styles.modalActions}>
            <Button
              label='Clear'
              variant=''
              onClick={() => {
                setSelectedFields([]);
                setResetTrigger((prev) => !prev);
              }}
            />
            <Button
              label='Select All'
              variant=''
              onClick={() => {
                const allFields = (record?.connectionInfo || []).map(
                  (f) => f.fieldName
                );
                setSelectedFields(allFields);
                setResetTrigger((prev) => !prev);
              }}
            />
          </div>
        }
        footerComponent={
          <p className={styles.syncText}>
            {selectedFields?.length ?? 0} /{' '}
            {(record?.connectionInfo || []).length} Selected
          </p>
        }
      />
    </>
  );
};

export default AgentRow;
